import React, { forwardRef, useState } from 'react';
import { Popover } from '@mui/material';
import { ButtonV2, CircleIcon } from '@bbkAdminComponents/buttons';
import { GenericActionMenuButton } from '@bbkAdminComponents/action-menu/generic-action-menu-button';
import { HoverIcons } from '@bbkAdminComponents/experiences/audiences/components';

export type Action = {
  icon?: React.ReactNode;
  copy?: React.ReactNode;
  onClick?: (...args: unknown[]) => unknown;
  disabled?: boolean;
  disabledText?: string;
};

type Props = {
  actions?: Action[];
  children?: (handleClose: () => unknown) => React.ReactNode;
  disabled?: boolean;
  actionButton?: typeof DefaultActionButton;
  popoverProps?: Omit<React.ComponentProps<typeof Popover>, 'open'>;
};

const DefaultActionButton: React.FC<{
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  disabled: boolean | undefined;
}> = ({ onClick, disabled }) => (
  <GenericActionMenuButton
    onClick={onClick}
    disabled={disabled}
    circle
    small
    data-testid="generic-action-menu-button"
  >
    {HoverIcons.tripleDots}
  </GenericActionMenuButton>
);

const Button: React.FC<
  {
    icon?: React.ReactNode;
    copy?: React.ReactNode;
  } & React.ComponentProps<typeof ButtonV2>
> = ({ icon, copy, ...restProps }) => (
  <ButtonV2
    transparent
    centerAll
    css={{
      paddingBottom: 0,
      justifyContent: 'left',
    }}
    {...restProps}
  >
    {icon && <CircleIcon className="tw-mr-1">{icon}</CircleIcon>}
    {copy}
  </ButtonV2>
);

export const GenericActionMenu = forwardRef<HTMLDivElement, Props>(
  (
    {
      disabled,
      actionButton: ActionButton = DefaultActionButton,
      popoverProps,
      ...restProps
    },
    ref
  ) => {
    const actions = restProps.actions;
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleOpen: React.MouseEventHandler<HTMLButtonElement> = (event) =>
      setAnchorEl(event.currentTarget);

    const handleClose = () => setAnchorEl(null);
    const content = restProps.children?.(handleClose);

    const renderedButtons = actions ? (
      <div className="tw-flex tw-flex-col">
        {actions.map(
          (
            {
              onClick,
              disabled: disabledAction,
              disabledText,
              ...restActionProps
            },
            idx
          ) => (
            <Button
              key={idx}
              onClick={() => {
                handleClose();
                onClick?.();
              }}
              disabled={disabledAction}
              title={disabledAction ? disabledText : ''}
              {...restActionProps}
            />
          )
        )}
      </div>
    ) : undefined;

    return (
      <div ref={ref}>
        <ActionButton onClick={handleOpen} disabled={disabled} />
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          {...popoverProps}
        >
          <div className="tw-pb-4 tw-pt-2">{content ?? renderedButtons}</div>
        </Popover>
      </div>
    );
  }
);
